import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import { supabase } from '../lib/supabaseClient';
import {
  JOB_STATUSES,
  JOB_STATUS_MAP,
  formatPhone,
  formatDate,
  timeAgo,
} from './constants';

import './CallInbox.css';
import './JobProgress.css';

// Granot deep links need a per-login session key, so the job # instead copies
// the number and opens Granot — paste into Quick Search there.
const GRANOT_URL = 'https://eagle.hellomoving.com/';

// Logins that never appear as Job Progress dispatchers (they keep full console
// access — this only hides them from the dispatcher dropdown/chips here).
const NON_DISPATCHERS = new Set([
  'info@mc-movers.com',      // Sales (shared bucket for the Calls board)
  '561delray@gmail.com',     // 561 Delray
  'chris@mc-movers.com',     // Chris
  'raz.sela10@gmail.com',    // Raz Sela
]);

const BLANK_FORM = {
  job_no: '',
  customer_name: '',
  phone: '',
  email: '',
  from_city: '',
  from_state: '',
  to_city: '',
  to_state: '',
  cf: '',
  est_total: '',
  move_date: '',
};

// Build "City, ST → City, ST" from whatever pieces exist.
function route(j) {
  const a = [j.from_city, j.from_state].filter(Boolean).join(', ');
  const b = [j.to_city, j.to_state].filter(Boolean).join(', ');
  if (a && b) return `${a} → ${b}`;
  return a || b || '';
}

export default function JobProgress() {
  const [jobs, setJobs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | <statusKey>
  const [dispatcherFilter, setDispatcherFilter] = useState('all'); // 'all' | 'unassigned' | <id>
  const [expanded, setExpanded] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [savingNew, setSavingNew] = useState(false);

  // ---- Initial load ------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    const [jobsRes, staffRes] = await Promise.all([
      supabase
        .from('jobs')
        .select('*')
        .order('move_date', { ascending: true, nullsFirst: false })
        .limit(1000),
      supabase.from('profiles').select('id, full_name, email, role'),
    ]);
    if (jobsRes.error) setError(jobsRes.error.message);
    setJobs(jobsRes.data || []);
    setStaff(staffRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Realtime: jobs stream in / update live ----------------------------
  useEffect(() => {
    const channel = supabase
      .channel('jobs-stream')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, (payload) => {
        setJobs((prev) => {
          if (payload.eventType === 'INSERT') {
            if (prev.some((j) => j.id === payload.new.id)) return prev;
            return [payload.new, ...prev];
          }
          if (payload.eventType === 'UPDATE') {
            return prev.map((j) => (j.id === payload.new.id ? { ...j, ...payload.new } : j));
          }
          if (payload.eventType === 'DELETE') {
            return prev.filter((j) => j.id !== payload.old.id);
          }
          return prev;
        });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Dispatcher options, sorted by name. Some logins (owner, shared inboxes,
  // sales-only) don't dispatch jobs and are kept out of the list.
  const dispatchers = useMemo(
    () =>
      staff
        .filter((s) => !NON_DISPATCHERS.has((s.email || '').toLowerCase()))
        .sort((a, b) =>
          (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')
        ),
    [staff]
  );
  const staffById = useMemo(() => {
    const m = {};
    staff.forEach((s) => (m[s.id] = s));
    return m;
  }, [staff]);

  // ---- Edit a job (dispatcher / status / pickup / details) ---------------
  const updateJob = useCallback(
    async (id, patch) => {
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
      const { error: err } = await supabase.from('jobs').update(patch).eq('id', id);
      if (err) {
        setError(err.message);
        load();
      }
    },
    [load]
  );

  const deleteJob = useCallback(
    async (id) => {
      if (!window.confirm('Delete this job?')) return;
      setJobs((prev) => prev.filter((j) => j.id !== id));
      const { error: err } = await supabase.from('jobs').delete().eq('id', id);
      if (err) {
        setError(err.message);
        load();
      }
    },
    [load]
  );

  // ---- Manual add --------------------------------------------------------
  const addJob = async () => {
    if (!form.job_no.trim() && !form.customer_name.trim()) {
      setError('Add at least a job # or a customer name.');
      return;
    }
    setSavingNew(true);
    const num = (v) => (v !== '' && v != null && isFinite(Number(v)) ? Number(v) : null);
    const s = (v) => (v && v.trim() ? v.trim() : null);
    const payload = {
      job_no: s(form.job_no),
      customer_name: s(form.customer_name),
      phone: s(form.phone),
      email: s(form.email),
      from_city: s(form.from_city),
      from_state: s(form.from_state),
      to_city: s(form.to_city),
      to_state: s(form.to_state),
      cf: num(form.cf),
      est_total: num(form.est_total),
      move_date: form.move_date || null,
      status: 'booked',
      source: 'manual',
    };
    const { error: err } = await supabase.from('jobs').insert(payload);
    setSavingNew(false);
    if (err) {
      setError(err.message.includes('duplicate') ? `Job ${form.job_no} already exists.` : err.message);
      return;
    }
    setForm(BLANK_FORM);
    setAdding(false);
    setError('');
  };

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ---- Counts + filtering ------------------------------------------------
  const counts = useMemo(() => {
    const byStatus = {};
    JOB_STATUSES.forEach((s) => (byStatus[s.key] = 0));
    let active = 0;
    jobs.forEach((j) => {
      byStatus[j.status] = (byStatus[j.status] || 0) + 1;
      if (j.status !== 'delivered') active += 1;
    });
    return { byStatus, active, all: jobs.length };
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = jobs.filter((j) => {
      if (statusFilter === 'active' && j.status === 'delivered') return false;
      if (statusFilter !== 'all' && statusFilter !== 'active' && j.status !== statusFilter) return false;
      if (dispatcherFilter === 'unassigned' && j.dispatcher_id) return false;
      if (
        dispatcherFilter !== 'all' &&
        dispatcherFilter !== 'unassigned' &&
        j.dispatcher_id !== dispatcherFilter
      )
        return false;
      if (!q) return true;
      const hay = `${j.job_no || ''} ${j.customer_name || ''} ${j.phone || ''} ${j.email || ''} ${
        j.from_city || ''
      } ${j.to_city || ''} ${j.details || ''}`.toLowerCase();
      return hay.includes(q);
    });
    // Problem-flagged jobs float to the top; the rest keep move-date order.
    return [...list].sort((a, b) => (b.flagged === true) - (a.flagged === true));
  }, [jobs, query, statusFilter, dispatcherFilter]);

  const lastSynced = useMemo(() => {
    let t = null;
    jobs.forEach((j) => {
      if (j.synced_at && (!t || new Date(j.synced_at) > new Date(t))) t = j.synced_at;
    });
    return t;
  }, [jobs]);

  const colCount = 7;

  const actions = (
    <>
      <input
        className="input sub-nav-search"
        type="search"
        placeholder="Search job #, customer, city…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
        {adding ? 'Close' : '+ Add job'}
      </button>
    </>
  );

  return (
    <AdminLayout title="Job Progress" actions={actions}>
      {error && <div className="board-error">{error}</div>}

      {adding && (
        <div className="job-add">
          <div className="job-add-grid">
            <label>
              Job #
              <input value={form.job_no} onChange={(e) => setForm({ ...form, job_no: e.target.value })} placeholder="MC3704110" />
            </label>
            <label>
              Customer
              <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Full name" />
            </label>
            <label>
              Phone
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
            </label>
            <label>
              Email
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@email.com" />
            </label>
            <label>
              Move date
              <input type="date" value={form.move_date} onChange={(e) => setForm({ ...form, move_date: e.target.value })} />
            </label>
            <label>
              From city
              <input value={form.from_city} onChange={(e) => setForm({ ...form, from_city: e.target.value })} />
            </label>
            <label>
              From ST
              <input value={form.from_state} maxLength={2} onChange={(e) => setForm({ ...form, from_state: e.target.value.toUpperCase() })} />
            </label>
            <label>
              To city
              <input value={form.to_city} onChange={(e) => setForm({ ...form, to_city: e.target.value })} />
            </label>
            <label>
              To ST
              <input value={form.to_state} maxLength={2} onChange={(e) => setForm({ ...form, to_state: e.target.value.toUpperCase() })} />
            </label>
            <label>
              Cubic ft
              <input value={form.cf} onChange={(e) => setForm({ ...form, cf: e.target.value })} placeholder="cf" />
            </label>
            <label>
              Est. total
              <input value={form.est_total} onChange={(e) => setForm({ ...form, est_total: e.target.value })} placeholder="$" />
            </label>
          </div>
          <div className="job-add-foot">
            <button className="btn btn-primary" onClick={addJob} disabled={savingNew}>
              {savingNew ? 'Saving…' : 'Add job'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setAdding(false); setForm(BLANK_FORM); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {!loading && (
        <div className="filter-bar">
          <div className="scope-tabs">
            <button className={statusFilter === 'active' ? 'st-tab active' : 'st-tab'} onClick={() => setStatusFilter('active')}>
              Active <span className="ab-count">{counts.active}</span>
            </button>
            <button className={statusFilter === 'all' ? 'st-tab active' : 'st-tab'} onClick={() => setStatusFilter('all')}>
              All <span className="ab-count">{counts.all}</span>
            </button>
            {JOB_STATUSES.map((s) => (
              <button
                key={s.key}
                className={statusFilter === s.key ? 'st-tab active' : 'st-tab'}
                onClick={() => setStatusFilter(s.key)}
              >
                {s.label} <span className="ab-count">{counts.byStatus[s.key] || 0}</span>
              </button>
            ))}
          </div>
          <div className="assignee-bar">
            <button className={dispatcherFilter === 'all' ? 'ab-chip active' : 'ab-chip'} onClick={() => setDispatcherFilter('all')}>
              All dispatchers
            </button>
            <button className={dispatcherFilter === 'unassigned' ? 'ab-chip active' : 'ab-chip'} onClick={() => setDispatcherFilter('unassigned')}>
              Unassigned
            </button>
            {dispatchers.map((s) => (
              <button
                key={s.id}
                className={dispatcherFilter === s.id ? 'ab-chip active' : 'ab-chip'}
                onClick={() => setDispatcherFilter(s.id)}
              >
                {s.full_name || s.email}
              </button>
            ))}
          </div>
          {lastSynced && (
            <div className="sync-note">Last Granot sync: {timeAgo(lastSynced)}</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="board-empty">Loading jobs…</div>
      ) : (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="col-caller">Job / Customer</th>
                <th>Contact</th>
                <th>Route</th>
                <th>Move date</th>
                <th className="col-assignee">Dispatcher</th>
                <th className="col-status">Status</th>
                <th className="col-flag">Flag</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="ui-empty">
                    No jobs yet. Booked jobs will sync in from Granot daily — or add one manually.
                  </td>
                </tr>
              )}

              {filtered.map((j) => {
                const isOpen = expanded.has(j.id);
                return (
                  <React.Fragment key={j.id}>
                    <tr
                      className={`ui-row${j.status === 'delivered' ? ' resolved' : ''}${
                        j.flagged ? ' job-flagged' : ''
                      }`}
                    >
                      {/* Job # + customer */}
                      <td className="col-caller">
                        <div className="caller-cell">
                          <button className="disclosure" onClick={() => toggle(j.id)} aria-label="expand">
                            {isOpen ? '▾' : '▸'}
                          </button>
                          <div className="caller-main">
                            <div className="caller-name">
                              <span className="caller-name-text">{j.customer_name || '—'}</span>
                              {j.source === 'manual' && <span className="src-badge manual">Manual</span>}
                              {j.flagged && <span className="flag-badge">⚑ problem</span>}
                            </div>
                            {j.job_no && (
                              <a
                                className="job-sub job-link"
                                href={GRANOT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Copies the job # and opens Granot — paste into Quick Search"
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(j.job_no.replace(/^MC/i, ''));
                                  } catch {
                                    /* clipboard unavailable — link still opens Granot */
                                  }
                                }}
                              >
                                {j.job_no} ↗
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td>
                        {j.phone ? (
                          <a href={`tel:${j.phone}`} className="callback-link">{formatPhone(j.phone)}</a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {j.email && (
                          <div className="job-email">
                            <a href={`mailto:${j.email}`}>{j.email}</a>
                          </div>
                        )}
                      </td>

                      {/* Route */}
                      <td>{route(j) || <span className="muted">—</span>}</td>

                      {/* Move date — editable; a manual edit locks it against the
                          daily Granot refresh */}
                      <td>
                        <input
                          type="date"
                          className="job-date-input"
                          value={j.move_date || ''}
                          title={j.move_date_edited ? 'Edited by staff — Granot sync will not change it' : 'From Granot — edit if the customer moved the date'}
                          onChange={(e) =>
                            updateJob(j.id, { move_date: e.target.value || null, move_date_edited: true })
                          }
                        />
                      </td>

                      {/* Dispatcher */}
                      <td className="col-assignee">
                        <select
                          className="ui-select"
                          value={j.dispatcher_id || ''}
                          onChange={(e) => updateJob(j.id, { dispatcher_id: e.target.value || null })}
                        >
                          <option value="">Unassigned</option>
                          {dispatchers.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.full_name || s.email}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Status */}
                      <td className="col-status">
                        <span className="status-pill" style={{ '--pill': JOB_STATUS_MAP[j.status]?.color || '#999' }}>
                          <select
                            className="ui-select pill-select"
                            value={j.status}
                            onChange={(e) => updateJob(j.id, { status: e.target.value })}
                          >
                            {JOB_STATUSES.map((s) => (
                              <option key={s.key} value={s.key}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </span>
                      </td>

                      {/* Problem flag — turns the row red and floats it up */}
                      <td className="col-flag">
                        <button
                          className={j.flagged ? 'flag-btn on' : 'flag-btn'}
                          title={j.flagged ? 'Problem flagged — click to clear' : 'Flag this job as having problems'}
                          onClick={() => updateJob(j.id, { flagged: !j.flagged })}
                        >
                          ⚑
                        </button>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="detail-row">
                        <td colSpan={colCount}>
                          <div className="detail">
                            {/* Exact dates: when it was actually picked up / delivered */}
                            <div className="da-row">
                              <div className="da-field">
                                <label>Picked up on</label>
                                <input
                                  type="date"
                                  className="job-date-input"
                                  value={j.pickup_date || ''}
                                  onChange={(e) => updateJob(j.id, { pickup_date: e.target.value || null })}
                                />
                              </div>
                              <div className="da-field">
                                <label>Delivered on</label>
                                <input
                                  type="date"
                                  className="job-date-input"
                                  value={j.delivered_date || ''}
                                  onChange={(e) => updateJob(j.id, { delivered_date: e.target.value || null })}
                                />
                              </div>
                            </div>
                            <div className="da-field da-notes">
                              <label>Job details / notes</label>
                              <textarea
                                className="notes-input"
                                defaultValue={j.details || ''}
                                placeholder="Pickup window, crew, storage, special instructions…"
                                rows={3}
                                onBlur={(e) => {
                                  const v = e.target.value.trim();
                                  if (v !== (j.details || '')) updateJob(j.id, { details: v || null });
                                }}
                              />
                            </div>
                            <div className="job-detail-meta">
                              {j.cf != null && <span>{j.cf} cf</span>}
                              {j.booked_date && <span>Booked {formatDate(j.booked_date)}</span>}
                              {j.dispatcher_id && staffById[j.dispatcher_id] && (
                                <span>Dispatcher: {staffById[j.dispatcher_id].full_name || staffById[j.dispatcher_id].email}</span>
                              )}
                              <span>Source: {j.source === 'manual' ? 'Manual entry' : 'Granot'}</span>
                              <button className="job-delete" onClick={() => deleteJob(j.id)}>
                                Delete job
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
